import {Box, IconButton, TextField, Typography, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, FormControl, Select, MenuItem} from "@mui/material";
import {EditOutlined as EditIcon, DeleteOutline as DeleteIcon, Add as AddIcon, LocalOfferOutlined as TagIcon} from "@mui/icons-material";
import {useState, useEffect, useCallback, useImperativeHandle, forwardRef} from "react";
import {t} from "../dashboard/coach/coachTokens";
import MyDialog from "../shared/Dialog";
import {useAuth} from "../../contexts/AuthContext";
import {useCompany} from "../../contexts/CompanyContext";

// Tags are name-only (no icon/image). This mirrors ResourceCategoryManager's structure —
// same forwardRef + useImperativeHandle({openCreate}) contract and t.* styling — but strips
// the icon grid, image uploader and their table columns.
const td = {padding: "12px 14px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 14px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

const ResourceTagManager = forwardRef(({config, companyId, onTagsChange}, ref) => {
    const {tagService, labels} = config;

    const {isSuperAdmin} = useAuth();
    const {getCompaniesList} = useCompany();

    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [name, setName] = useState("");
    const [dialogCompanyId, setDialogCompanyId] = useState("");
    const [saving, setSaving] = useState(false);

    // Super admins must pick a company when creating (editing keeps the existing company).
    const requiresCompanySelection = isSuperAdmin() && !editingId;

    const fetchTags = useCallback(async () => {
        setLoading(true);
        try {
            const res = await tagService.getTags(companyId);
            const list = res.data || [];
            setTags(list);
            if (onTagsChange) onTagsChange(list);
        } catch (e) {
            console.error("Failed to fetch tags:", e);
        } finally {
            setLoading(false);
        }
    }, [tagService, companyId, onTagsChange]);

    useEffect(() => {
        fetchTags();
    }, [fetchTags]);

    const openCreate = () => {
        setEditingId(null);
        setName("");
        setDialogCompanyId(companyId || "");
        setDialogOpen(true);
    };

    // Expose openCreate to parent via ref
    useImperativeHandle(ref, () => ({
        openCreate
    }));

    const handleSave = async () => {
        if (!name.trim()) return;
        const effectiveCompanyId = isSuperAdmin() ? dialogCompanyId : companyId;
        if (requiresCompanySelection && !effectiveCompanyId) return;
        setSaving(true);
        try {
            if (editingId) {
                await tagService.updateTag(editingId, {name: name.trim()});
            } else {
                await tagService.saveTag({name: name.trim(), companyId: effectiveCompanyId});
            }
            closeDialog();
            fetchTags();
        } catch (e) {
            console.error("Failed to save tag:", e);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(labels.tagDeleteConfirm)) return;
        try {
            await tagService.deleteTag(id);
            fetchTags();
        } catch (e) {
            console.error("Failed to delete tag:", e);
        }
    };

    const startEdit = (tag) => {
        setEditingId(tag.id);
        setName(tag.name);
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditingId(null);
        setName("");
        setDialogCompanyId("");
    };

    return (
        <Box sx={{height: "100%", display: "flex", flexDirection: "column"}}>
            {/* Create/Edit dialog */}
            <MyDialog
                isOpen={dialogOpen}
                close={closeDialog}
                title={editingId ? "עריכת תגית" : "הוספת תגית"}
                showCloseButton={true}
                isTitleCentered={false}
                narrow={true}
                actions={
                    <Box
                        component="button"
                        onClick={handleSave}
                        disabled={!name.trim() || saving || (requiresCompanySelection && !dialogCompanyId)}
                        sx={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            background: t.green, color: "#fff", height: 38, px: "22px", borderRadius: t.rs,
                            fontSize: "14px", fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit",
                            transition: "all .14s",
                            "&:hover": {background: t.greenDark},
                            "&:disabled": {background: t.border, color: t.t3, cursor: "not-allowed"},
                        }}
                    >
                        {saving ? "שומר..." : editingId ? "עדכון" : "הוספה"}
                    </Box>
                }
            >
                <Box sx={{padding: "20px 0", display: "flex", flexDirection: "column", gap: "20px", direction: "rtl"}}>
                    {requiresCompanySelection && (
                        <Box>
                            <Typography sx={{fontSize: "14px", fontWeight: 600, color: t.text, mb: 1, textAlign: "right"}}>
                                חברה <span style={{color: t.red}}>*</span>
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={dialogCompanyId}
                                    onChange={e => setDialogCompanyId(e.target.value)}
                                    displayEmpty
                                    sx={{
                                        borderRadius: t.rs, backgroundColor: t.white,
                                        "&:hover .MuiOutlinedInput-notchedOutline": {borderColor: t.green},
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {borderColor: t.green},
                                    }}
                                >
                                    <MenuItem value="" disabled>בחר חברה</MenuItem>
                                    {getCompaniesList().map(comp => (
                                        <MenuItem key={comp.id} value={comp.id}>{comp.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                    <Box>
                        <Typography sx={{fontSize: "14px", fontWeight: 600, color: t.text, mb: 1, textAlign: "right"}}>
                            שם תגית <span style={{color: t.red}}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="למשל: מתחילים"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: t.rs, backgroundColor: t.white,
                                    "& fieldset": {borderColor: t.border},
                                    "&:hover fieldset": {borderColor: t.green},
                                    "&.Mui-focused fieldset": {borderColor: t.green},
                                }
                            }}
                        />
                    </Box>
                </Box>
            </MyDialog>

            {/* Card */}
            <Box sx={{bgcolor: t.white, border: `1px solid ${t.border}`, borderRadius: t.r, overflow: "hidden", boxShadow: t.sh}}>
                {/* Header band */}
                <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "14px 18px", borderBottom: `1px solid ${t.border}`, background: t.bg, direction: "rtl",
                }}>
                    <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
                        <Box sx={{width: 36, height: 36, borderRadius: "10px", background: t.white, border: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "center"}}>
                            <TagIcon sx={{color: t.t2, fontSize: 20}}/>
                        </Box>
                        <Box>
                            <Typography sx={{fontSize: 15, fontWeight: 800, color: t.text, letterSpacing: "-0.2px"}}>{labels.tagsCardTitle}</Typography>
                            <Typography sx={{fontSize: 11.5, color: t.t3, fontWeight: 600}}>{tags.length} תגיות</Typography>
                        </Box>
                    </Box>
                    <Box component="button" onClick={openCreate} sx={{
                        display: "inline-flex", alignItems: "center", gap: "5px",
                        background: t.green, color: "#fff", height: 34, px: "15px", borderRadius: t.rs,
                        fontSize: "13px", fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit",
                        transition: "all .14s", "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
                    }}>
                        <AddIcon sx={{fontSize: 16}}/>
                        {labels.newTagButton}
                    </Box>
                </Box>

                {loading && !tags.length ? (
                    <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, py: 6}}>
                        <CircularProgress size={32} sx={{color: t.green}}/>
                    </Box>
                ) : !tags.length ? (
                    <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, py: 6}}>
                        <TagIcon sx={{fontSize: 48, color: t.border}}/>
                        <Typography sx={{color: t.t3, fontSize: "15px", fontWeight: 500}}>אין תגיות</Typography>
                        <Typography sx={{color: t.t3, fontSize: "13px"}}>לחץ על "{labels.newTagButton}" כדי להוסיף</Typography>
                    </Box>
                ) : (
                    <TableContainer sx={{overflowY: "auto", maxHeight: 540, direction: "rtl"}}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{...th, textAlign: "right"}}>שם</TableCell>
                                    <TableCell sx={{...th, textAlign: "center", width: 110}}>פעולות</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tags.map(tag => (
                                    <TableRow key={tag.id} sx={{"&:hover td": {background: t.greenLight}, transition: "background .12s ease"}}>
                                        <TableCell sx={{...td, textAlign: "right", fontWeight: 600, color: t.text}}>
                                            {tag.name}
                                        </TableCell>
                                        <TableCell sx={{...td, textAlign: "center"}}>
                                            <Box sx={{display: "inline-flex", justifyContent: "center", gap: "2px"}}>
                                                <IconButton size="small" onClick={() => startEdit(tag)} sx={{color: t.t2, "&:hover": {color: t.green, bgcolor: t.greenLight}}}>
                                                    <EditIcon fontSize="small"/>
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleDelete(tag.id)} sx={{color: t.red, "&:hover": {bgcolor: t.redLight}}}>
                                                    <DeleteIcon fontSize="small"/>
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </Box>
    );
});

export default ResourceTagManager;
